import {ApiClient, IConfig, IResult, QueryArg} from 'jsonapi-react';
import {snakeCase} from 'lodash';
import {tokenSerializer} from './token';
import mapKeysToCase from './mapKeysToCase';
import getCircularReplacer from './getCircularReplacer';
import {getEnv} from '../config/environment';
import {BASE_CONFIG} from '../config/http';
import schema from '../schema';

const OVERRIDE_KEYS = {
  s_3_image_url: 's3_image_url',
};

export class JsonApiError extends Error {
  status: number | null;

  code: number | null;

  constructor(error: any) {
    super(
      typeof error === 'object'
        ? error.message || error.detail || error.title
        : error
    );
    this.name = 'JsonApiError';
    this.status = typeof error === 'object' ? error.status : null;
    this.code = typeof error === 'object' ? error.status : null;

    Object.setPrototypeOf(this, JsonApiError.prototype);
  }
}

async function fetchWithToken(path: string, config: RequestInit) {
  const newConfig = {
    ...config,
    headers: {
      ...config.headers,
      ...BASE_CONFIG.headers,
      ...(await tokenSerializer()),
    },
  };

  return fetchWithStatusCheck(path, newConfig);
}

// This is so the HTTP status code can be used in application logic. The promise returned by this function will be rejected
// if the HTTP status code is 400 or higher.
// See https://github.com/aribouius/jsonapi-react/blob/025e7f64eb106de2ab1d41cb9812e5cc1a4b294a/src/client.js#L377
async function fetchWithStatusCheck(path: string, config: RequestInit) {
  const r = await fetch(path, config);

  if (r.status >= 400) {
    const errorMessageBody = await r.text();
    // Extract error message from JSON response or plain text response
    let errorMessage;
    try {
      const errorMessageJson = JSON.parse(errorMessageBody);
      // Non-JSON:API endpoints (e.g. paid_gigs/:id/swap_match) render `errors` as
      // bare strings rather than objects carrying `detail`.
      const [firstError] = errorMessageJson.errors;
      errorMessage =
        typeof firstError === 'string' ? firstError : firstError.detail;
    } catch (e) {
      errorMessage = errorMessageBody;
    }
    // Pass error message and status to JsonApiError, so it can be used in application logic
    throw new JsonApiError({status: r.status, message: errorMessage});
  }
  return r;
}

const API_URL =
  getEnv().VITE_API_HOST || 'http://api.local.popularpays.com:3000';

const jsonApiClient = new ApiClient({
  url: API_URL,
  schema,
  cacheTime: getEnv().MODE === 'test' ? 0 : 5 * 60,
  staleTime: 60,
  fetch: fetchWithToken,
});

export async function jsonApiFetch(queryArg: QueryArg, config = {}) {
  const results = await jsonApiClient.fetch(queryArg, config);
  if (results?.error) {
    throw new JsonApiError(results.error);
  }
  // remove circular references from the returned data + map the results data keys to camelCase
  return JSON.parse(
    JSON.stringify(mapKeysToCase(results), getCircularReplacer())
  );
}

export async function jsonApiMutate(
  queryArg: QueryArg,
  data: any,
  config = {}
) {
  const serializedData: any = mapKeysToCase(data, snakeCase);

  // Check serializedData against overrideKeys
  for (const [key, value] of Object.entries(OVERRIDE_KEYS)) {
    if (Object.prototype.hasOwnProperty.call(serializedData, key)) {
      serializedData[value] = serializedData[key];
      delete serializedData[key];
    }
  }

  const results = await jsonApiClient.mutate(queryArg, serializedData, config);
  if (results?.error) {
    throw new JsonApiError(results.error);
  }
  return mapKeysToCase(results);
}

// POST/PUT a pre-built request body through the authenticated fetch wrapper, bypassing
// jsonapi-react's mutate serializer. Needed in two cases:
//   - `relationships` + `included` for new nested resources, which the content-submission
//     create endpoint requires to attach mixed-media children (see api/updaters/contentSubmissions).
//   - endpoints that read flat top-level params instead of a JSON:API document, which the
//     serializer cannot emit (see api/socialPosts).
export async function jsonApiRawMutate(
  path: string,
  document: object,
  method: 'POST' | 'PUT' = 'POST',
  contentType = 'application/vnd.api+json'
) {
  const response = await fetchWithToken(`${API_URL}${path}`, {
    method,
    body: JSON.stringify(document),
    headers: {'Content-Type': contentType},
  });
  return mapKeysToCase(await response.json());
}

export async function jsonApiDelete(queryArg: QueryArg, config = {}) {
  const results = await jsonApiClient.delete(queryArg, config);
  if (results?.error) {
    throw new JsonApiError(results.error);
  }
  return JSON.parse(
    JSON.stringify(mapKeysToCase(results), getCircularReplacer())
  );
}

export default jsonApiClient;
